import { configTypeForProvider, configTypeName, providerIcon, providerKey } from '@shell/models/management.cattle.io.authconfig';
import { LOCAL_AUTH_ID, UNSUPPORTED_AUTH_IDS } from '@shell/utils/auth';
import { clone } from '@shell/utils/object';
import { sortBy } from '@shell/utils/sort';

export const LOCAL_PROVIDER = 'localProvider';
export const REMEMBERED_PROVIDER_KEY = 'rancher-login-provider';

export interface AuthProviderDriver {
  id: string;
  type: string;
  /** Free text the admin gave the config, when the server has one for it. */
  description?: string;
}

export interface AuthProviderOption {
  id: string;
  type: string;
  key: string;
  category?: string;
  name: string;
  description: string;
  meta: string;
  icon: string;
  isLocal: boolean;
}

interface I18n {
  t: (key: string, args?: object) => string;
  withFallback: (key: string, args: object | null, fallback: string) => string;
}

const displayNameFor = (id: string, key: string, vendorLabel: string): string => {
  return id.toLowerCase() === key ? vendorLabel : id;
};

/**
 * The providers to offer on the login page, in the order they are offered.
 *
 * Built from `/v1-public/authproviders`, which is all an unauthenticated visitor
 * can see: an id, a provider type, and whatever the admin wrote about the config.
 */
export const toProviderOptions = (drivers: AuthProviderDriver[], i18n: I18n): AuthProviderOption[] => {
  const { t, withFallback } = i18n;

  const external = sortBy(
    drivers.filter((driver) => driver.type !== LOCAL_PROVIDER),
    ['type', 'id']
  ).map((driver: AuthProviderDriver): AuthProviderOption => {
    const key = providerKey(driver.type);
    const category = configTypeForProvider(driver.type);
    const vendorLabel = withFallback(`model.authConfig.provider."${ key }"`, null, key);
    const protocolLabel = category ? withFallback(`model.authConfig.description."${ category }"`, null, category.toUpperCase()) : '';

    return {
      id:          driver.id,
      type:        driver.type,
      key,
      category,
      name:        displayNameFor(driver.id, key, vendorLabel),
      description: driver.description || '',
      meta:        protocolLabel ? t('login.providers.meta', { vendor: vendorLabel, protocol: protocolLabel }) : vendorLabel,
      icon:        providerIcon(driver.type),
      isLocal:     false,
    };
  });

  const hasLocal = drivers.some((driver) => driver.type === LOCAL_PROVIDER);

  if (!hasLocal) {
    return external;
  }

  return [
    ...external,
    {
      id:          LOCAL_AUTH_ID,
      type:        LOCAL_PROVIDER,
      key:         LOCAL_AUTH_ID,
      category:    '',
      name:        t('login.providers.local.name'),
      description: t('login.providers.local.description'),
      meta:        t('login.providers.local.meta'),
      icon:        providerIcon(LOCAL_PROVIDER),
      isLocal:     true,
    },
  ];
};

/** What the server says about a provider type it supports. */
export interface AuthProviderTypeMeta {
  Description?: string;
  Type?: string;
}

export interface AuthProviderType {
  /** The provider, e.g. `github`. Names the form, the route and the tile. */
  id: string;
  /** The type an authconfig of this provider carries, e.g. `githubConfig`. */
  configTypeName: string;
  name: string;
  category: string;
  categoryLabel: string;
  icon: string;
}

/**
 * The catalogue of provider types that can be configured, one entry per type.
 *
 * Built from `/v1-public/authprovider-types`. Rancher used to pre-create an empty
 * authconfig per supported type and the catalogue was read off those; nothing is
 * pre-created any more, so the server is asked what it supports.
 */
export const toProviderTypes = (types: Record<string, AuthProviderTypeMeta>, i18n: Pick<I18n, 'withFallback'>): AuthProviderType[] => {
  const { withFallback } = i18n;

  const out = Object.keys(types || {}).reduce((acc: AuthProviderType[], type) => {
    const id = providerKey(type);

    if (!id || id === LOCAL_AUTH_ID || UNSUPPORTED_AUTH_IDS.includes(id)) {
      return acc;
    }

    // The UI's own categorisation wins where it has one: the server calls Entra ID
    // OIDC, but the form that configures it and the save path it takes are the
    // OAuth ones. A provider the UI has never heard of is taken on trust.
    const category = configTypeForProvider(type) ?? (types[type]?.Type || '');

    acc.push({
      id,
      configTypeName: configTypeName(type),
      name:           withFallback(`model.authConfig.provider."${ id }"`, null, id),
      category,
      categoryLabel:  category ? withFallback(`model.authConfig.description."${ category }"`, null, category.toUpperCase()) : '',
      icon:           providerIcon(type),
    });

    return acc;
  }, []);

  return sortBy(out, ['categoryLabel', 'name']);
};

export const getRememberedProviderId = (): string | null => {
  try {
    return window.localStorage.getItem(REMEMBERED_PROVIDER_KEY);
  } catch (e) {
    return null;
  }
};

export const setRememberedProviderId = (id: string): void => {
  try {
    window.localStorage.setItem(REMEMBERED_PROVIDER_KEY, id);
  } catch (e) {}
};

export const clearRememberedProviderId = (): void => {
  try {
    window.localStorage.removeItem(REMEMBERED_PROVIDER_KEY);
  } catch (e) {}
};

/**
 * The provider the page should open on: the one the visitor asked to be
 * remembered, or the first on offer when there is no usable saved choice.
 */
export const resolveInitialProvider = (
  options: AuthProviderOption[],
  rememberedId: string | null
): AuthProviderOption | undefined => {
  const remembered = rememberedId ? options.find((option) => option.id === rememberedId) : undefined;

  return remembered || options[0];
};

/**
 * A free authconfig name for a new instance of a provider type.
 *
 * The name is the config's `metadata.name`, which the API will not let you change
 * after creation, so it has to be settled before the config is written. The first
 * config of a provider takes the provider's own key; the rest are suffixed.
 */
export const nextAuthConfigName = (takenIds: string[], key: string): string => {
  const taken = new Set(takenIds);

  if (!taken.has(key)) {
    return key;
  }

  let suffix = 2;

  while (taken.has(`${ key }-${ suffix }`)) {
    suffix++;
  }

  return `${ key }-${ suffix }`;
};

/**
 * The authconfig a principal belongs to. Principal ids are prefixed with the
 * config's name, not its provider type: `github-2_user://123` is a GitHub user of
 * the `github-2` config.
 */
export const configNameOfPrincipal = (principalId: string): string => principalId.replace(/:.*$/, '').split('_')[0].toLowerCase();

/**
 * The provider type of an authconfig, looked up by name in the loaded
 * `/v1-public/authproviders`. That only lists enabled configs, so the name is
 * returned as is when the config isn't found.
 */
export const providerOfConfig = (getters: Record<string, any>, configName: string): string => {
  const driver = getters['rancher/byId']('authProvider', configName);

  return driver?.type ? providerKey(driver.type) : configName;
};

/**
 * Fields a cloned config does not take from its source: its identity, whether and
 * to whom it is enabled, and anything secret.
 */
const UNCLONED_FIELDS = [
  'id', 'name', 'type', 'baseType', 'links', 'actions', 'created', 'createdTS', 'creatorId', 'uuid', 'annotations', 'labels', 'status',
  'ownerReferences', 'removed', 'enabled', 'accessMode', 'allowedPrincipalIds',
];

const SECRET_FIELDS = ['clientSecret', 'privateKey', 'applicationSecret', 'oauthCredential', 'serviceAccountCredential', 'serviceAccountPassword', 'spKey'];

const withoutSecrets = (value: any): any => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return clone(value);
  }

  return Object.keys(value).reduce((out, key) => {
    if (!SECRET_FIELDS.includes(key)) {
      out[key] = withoutSecrets(value[key]);
    }

    return out;
  }, {} as Record<string, any>);
};

/**
 * The settings of a Norman authconfig that a new config can start from.
 * Secrets are left out, whether the schema marks them as passwords or not and
 * wherever they are nested, so the admin enters them again for the new config.
 */
export const cloneableAuthConfigFields = (
  source: Record<string, any>,
  resourceFields: Record<string, { type?: string }>,
): Record<string, any> => Object.keys(resourceFields).reduce((fields, key) => {
  const isSecret = resourceFields[key]?.type === 'password' || SECRET_FIELDS.includes(key);

  if (!isSecret && !UNCLONED_FIELDS.includes(key) && source[key] !== undefined && source[key] !== null) {
    fields[key] = withoutSecrets(source[key]);
  }

  return fields;
}, {} as Record<string, any>);

/** `metadata.name` is a DNS label, and Rancher rejects anything else. */
export const isValidAuthConfigName = (name: string): boolean => /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name) && name.length <= 63;

/**
 * Whether an external provider can be switched on. Without the
 * `multiple-auth-providers` feature only one is enabled at a time, though
 * any number may be configured.
 *
 * @param configs every authconfig, local included
 * @param multipleAllowed whether the `multiple-auth-providers` feature is on
 * @param id the config being switched on, when it already exists
 */
export const canEnableAuthProvider = (
  configs: { id: string, enabled?: boolean }[],
  multipleAllowed: boolean,
  id?: string,
): boolean => multipleAllowed || !configs.some((config) => config.enabled && config.id !== LOCAL_AUTH_ID && config.id !== id);
